// src/components/popups/ClaimVendorProfileModal.js

import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Cookies from 'js-cookie';
import api from '../../api';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import Loader from '../Loader';
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';

// Вспомогательная функция для маскировки email
const maskEmail = (email) => {
    if (!email || !email.includes('@') || email.startsWith('@') || email.endsWith('@')) {
        return 'Invalid format';
    }
    const [userPart, domainPart] = email.split('@');
    const mask = (str, keepStart = 1, keepEnd = 1) => {
        if (!str) return '';
        const totalKeep = keepStart + keepEnd;
        if (str.length <= totalKeep) return str;
        const maskLength = Math.max(1, str.length - totalKeep);
        const start = str.substring(0, keepStart);
        const end = keepEnd > 0 ? str.substring(str.length - keepEnd) : '';
        const maskedSection = '*'.repeat(maskLength);
        return start + maskedSection + end;
    };
    if (!domainPart.includes('.')) {
        const maskedUser = mask(userPart, 1, 1);
        const maskedDomain = mask(domainPart, 1, 0);
        return `${maskedUser}@${maskedDomain}`;
    }
    const domainParts = domainPart.split('.');
    if (domainParts.length < 2 || !domainParts[0] || !domainParts[1]) return 'Invalid format';
    const domainName = domainParts[0];
    const domainTld = domainParts.slice(1).join('.');
    const maskedUser = mask(userPart, 1, 1);
    const maskedDomain = mask(domainName, 1, 0);
    return `${maskedUser}@${maskedDomain}.${domainTld}`;
};


// --- Основной компонент ---
export default function ClaimVendorProfileModal({ show, vendorToClaim, onClose }) {
    const { user, isAuthenticated } = useUser();
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [claimStep, setClaimStep] = useState('verifyEmail');
    const [enteredEmail, setEnteredEmail] = useState('');
    const [smsNotificationsConsent, setSmsNotificationsConsent] = useState(false);
    const [smsMarketingConsent, setSmsMarketingConsent] = useState(false);
    const [alternativeFormData, setAlternativeFormData] = useState({
        user_details: '',
        social_media_links: '',
        contact_email: '',
        contact_phone: '',
        description: '',
    });

    useEffect(() => {
        if (!show) {
            setTimeout(() => {
                setClaimStep('verifyEmail');
                setError(null);
                setLoading(false);
                setEnteredEmail('');
                setSmsNotificationsConsent(false);
                setAlternativeFormData({
                    user_details: '',
                    social_media_links: '',
                    contact_email: '',
                    contact_phone: '',
                    description: '',
                 });
            }, 300);
        }
    }, [show]);

    const vendorLegalEmail = vendorToClaim?.email;
    const canAttemptStandardClaim = !!vendorLegalEmail;
    const canSubmit = isAuthenticated && user && vendorToClaim?.vendor_id;

    const getRequestConfig = () => {
        const token = localStorage.getItem('authToken');
        const csrfToken = Cookies.get('csrftoken');
        const headers = {'Content-Type': 'application/json'};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }
        return {
            headers: headers,
            withCredentials: true,
        };
    };

    // --- Обработчики ---
    const handleEmailVerificationSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
             showNotification("Error", "Cannot submit claim. Ensure you are logged in.", null, "error");
             return;
        }
        if (!canAttemptStandardClaim) {
            setError("Vendor legal email is not available. Please use alternative verification.");
            return;
        }
        if (enteredEmail.toLowerCase() !== vendorLegalEmail.toLowerCase()) {
            setError("The entered email does not match the registered legal email for this vendor.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const config = getRequestConfig();
            const response = await api.post('/vendors-app/claim-request/', {
                vendor_id: vendorToClaim.vendor_id,
                sms_notifications_consent: smsNotificationsConsent,
                sms_marketing_consent: smsMarketingConsent,
            }, config);

            if (response.status === 200 || response.data.status === 'claim_initiated') {
                setClaimStep('submitted');
                showNotification("Claim Submitted", `Your request to claim "${vendorToClaim.name}" has been submitted. Please check the vendor's legal email inbox for verification steps.`, null, "success");
            } else {
                 setError(response.data.detail || "An unexpected error occurred.");
                 showNotification("Claim Error", response.data.detail || "Failed to submit claim request.", null, "error");
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || "An error occurred while submitting the claim.";
            setError(errorMsg);
            showNotification("Claim Error", errorMsg, null, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAlternativeFormChange = (e) => {
        const { name, value } = e.target;
        setAlternativeFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAlternativeSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            showNotification("Error", "Cannot submit claim. Ensure you are logged in.", null, "error");
            return;
        }
        const {user_details, social_media_links, contact_email, description } = alternativeFormData;
        if (!user_details || !social_media_links || !contact_email || !description) {
            setError("Please fill in all required fields to proceed with alternative verification.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const config = getRequestConfig();
            const payload = {
                vendor_id: vendorToClaim.vendor_id,
                ...alternativeFormData,
                sms_notifications_consent: smsNotificationsConsent,
                sms_marketing_consent: smsMarketingConsent,
            };
            const response = await api.post('/vendors-app/claim-requests/alternative/', payload, config);

             if (response.status === 201 || response.data.status === 'success') {
                 setClaimStep('submitted');
                 showNotification("Claim Submitted", `Your verification details for "${vendorToClaim.name}" have been submitted for review.`, null, "success");
             } else {
                 setError(response.data.message || "An unexpected error occurred during alternative submission.");
                 showNotification("Claim Error", response.data.message || "Failed to submit alternative claim request.", null, "error");
             }
        } catch (err) {
            const errorMsg = err.response?.data?.detail || "An error occurred while submitting the alternative claim.";
            setError(errorMsg);
            showNotification("Claim Error", errorMsg, null, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    if (!show) {
        return null;
    }

    const maskedVendorEmail = vendorLegalEmail ? maskEmail(vendorLegalEmail) : 'vendor\'s registered email';

    const isAlternativeFormComplete = alternativeFormData.user_details &&
                                      alternativeFormData.social_media_links &&
                                      alternativeFormData.contact_email &&
                                      alternativeFormData.description;

    const styles = `
        :root {
           --claim-modal-base-font-size: 16px;
           --claim-modal-scale-factor: 1.05;
        }
        .claim-modal-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background-color: rgba(0, 0, 0, 0.65);
            display: flex; align-items: center; justify-content: center;
            z-index: 1000; opacity: 0; animation: fadeIn 0.3s forwards;
            padding: 20px; box-sizing: border-box;
        }
        .claim-modal {
            background-color: #ffffff; border-radius: 12px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
            width: 100%; max-width: 600px; position: relative;
            display: flex; flex-direction: column; max-height: 90vh;
            overflow: hidden; opacity: 0; transform: translateY(-20px);
            animation: slideIn 0.3s 0.1s forwards;
            font-size: var(--claim-modal-base-font-size);
        }
        .claim-modal__close-btn {
            position: absolute; top: 12px; right: 12px;
            background: none; border: none; cursor: pointer;
            padding: 8px; line-height: 1; z-index: 11;
        }
        .claim-modal__close-btn img { width: 20px; height: 20px; opacity: 0.6; transition: opacity 0.2s ease; }
        .claim-modal__close-btn:hover img { opacity: 1; }
        .claim-modal__content {
            padding: 25px 30px; overflow-y: auto; flex-grow: 1; padding-bottom: 40px;
        }
        .claim-modal__header {
            text-align: center; margin-bottom: 20px; padding-bottom: 15px;
            border-bottom: 1px solid #eee; flex-shrink: 0;
            padding-left: 30px; padding-right: 30px; padding-top: 25px;
        }
        .claim-modal__title {
            font-size: calc(1.6em * var(--claim-modal-scale-factor));
            font-weight: 600; color: #333; margin: 0 0 8px 0;
        }
        .claim-modal__subtitle {
            font-size: calc(1em * var(--claim-modal-scale-factor));
            color: #666; margin: 0;
        }
        .claim-modal__vendor-name { font-weight: bold; color: #007bff; }
        .claim-modal__form-section { margin-bottom: 20px; }
        .claim-modal__form-section--center { text-align: center; }
        .claim-modal__label {
            display: block; margin-bottom: 8px; font-weight: 500;
            font-size: calc(0.95em * var(--claim-modal-scale-factor)); color: #555;
        }
        .claim-modal__input, .claim-modal__textarea {
            width: 100%; padding: 12px 15px; border: 1px solid #ccc;
            border-radius: 6px; font-size: calc(1em * var(--claim-modal-scale-factor));
            box-sizing: border-box; transition: border-color 0.2s ease, box-shadow 0.2s ease;
            margin-top: 2px;
        }
        .claim-modal__input:focus, .claim-modal__textarea:focus {
            outline: none; border-color: #007bff;
            box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
        }
        .claim-modal__textarea { resize: vertical; min-height: 100px; }
        .claim-modal__masked-vendor-email {
            font-size: calc(1.15em * var(--claim-modal-scale-factor)); font-weight: 600;
            color: #333; background-color: #f0f0f0; padding: 10px 14px;
            border-radius: 6px; display: inline-block; margin-top: 5px;
            border: 1px solid #ddd; word-break: break-all;
        }
        .claim-modal__info-text {
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
            color: #666; margin-bottom: 18px; line-height: 1.5;
        }
        .claim-modal__info-text--small {
             font-size: calc(0.85em * var(--claim-modal-scale-factor));
             margin-top: 15px; color: #777;
        }
        .claim-modal__error {
            color: #dc3545; background-color: #f8d7da; border: 1px solid #f5c6cb;
            padding: 12px 18px; border-radius: 6px; margin-bottom: 20px;
            font-size: calc(0.95em * var(--claim-modal-scale-factor)); text-align: center;
        }
        .claim-modal__actions {
            margin-top: 25px; display: flex; flex-direction: column; gap: 12px;
        }
        .claim-modal__content form .claim-modal__actions,
        .claim-modal__success-content .claim-modal__actions { padding-top: 10px; }
        .claim-modal__button {
            padding: 14px 22px; border-radius: 8px;
            font-size: calc(1em * var(--claim-modal-scale-factor)); font-weight: 500;
            cursor: pointer; border: none;
            transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease;
            width: 100%; box-sizing: border-box; text-align: center;
        }
        .claim-modal__button:active:not(:disabled) { transform: scale(0.98); }
        .claim-modal__button--primary { background-color: #007bff; color: white; }
        .claim-modal__button--primary:hover:not(:disabled) { background-color: #0056b3; }
        .claim-modal__button--primary:disabled { background-color: #a0cfff; cursor: not-allowed; opacity: 0.7; }
        .claim-modal__button--link {
            background: none; color: #007bff; text-align: center;
            padding: 10px; font-weight: normal;
            font-size: calc(0.95em * var(--claim-modal-scale-factor));
        }
        .claim-modal__button--link:hover:not(:disabled) { text-decoration: underline; color: #0056b3; }
        .claim-modal__button--link:disabled { color: #6c757d; cursor: not-allowed; opacity: 0.6; }
        .claim-modal__success-content { text-align: center; padding: 30px 20px; }
        .claim-modal__success-icon { width: 65px; height: 65px; margin-bottom: 20px; }
        .claim-modal__success-title {
             font-size: calc(1.7em * var(--claim-modal-scale-factor)); font-weight: 600;
             color: #28a745; margin-bottom: 15px;
        }
        .claim-modal__success-text {
            font-size: calc(1.05em * var(--claim-modal-scale-factor)); color: #555;
            line-height: 1.6; margin-bottom: 30px;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        .claim-modal .loader-container {
            position: absolute; top: 0; left: 0; right: 0; bottom: 0;
            background-color: rgba(255, 255, 255, 0.85); display: flex;
            align-items: center; justify-content: center;
            z-index: 10; border-radius: 12px;
        }
    `;

    return ReactDOM.createPortal(
        <>
            <style>{styles}</style>
            <div className="claim-modal-overlay" onClick={handleClose}>
                <div className="claim-modal" onClick={(e) => e.stopPropagation()}>
                    {loading && <Loader isModal={true} />}
                    <button onClick={handleClose} className="claim-modal__close-btn" aria-label="Close modal">
                        <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                    </button>

                    {(claimStep === 'verifyEmail' || claimStep === 'alternativeForm') && (
                         <div className="claim-modal__header">
                             <h2 className="claim-modal__title">
                                 {claimStep === 'verifyEmail' ? 'Claim Vendor Profile' : 'Alternative Verification'}
                            </h2>
                             {vendorToClaim && (
                                 <p className="claim-modal__subtitle">
                                     {claimStep === 'verifyEmail' ? 'Verify ownership of:' : 'Claiming:'} <span className="claim-modal__vendor-name">{vendorToClaim.name}</span>
                                 </p>
                             )}
                         </div>
                    )}

                    <div className="claim-modal__content">
                        {claimStep === 'verifyEmail' && (
                            <form onSubmit={handleEmailVerificationSubmit}>
                                {canAttemptStandardClaim ? (
                                    <>
                                        <div className="claim-modal__form-section claim-modal__form-section--center">
                                            <p className="claim-modal__info-text">
                                                To claim this profile, please verify the official email address associated with <span className="claim-modal__vendor-name">{vendorToClaim.name}</span>:
                                            </p>
                                            <div className="claim-modal__masked-vendor-email" aria-label="Masked vendor email">
                                                {maskedVendorEmail}
                                            </div>
                                        </div>
                                        <div className="claim-modal__form-section">
                                            <label htmlFor="emailVerification" className="claim-modal__label">
                                                Enter the vendor's full legal/official email address:
                                            </label>
                                            <input
                                                type="email"
                                                id="emailVerification"
                                                name="emailVerification"
                                                value={enteredEmail}
                                                onChange={(e) => { setEnteredEmail(e.target.value); setError(null); }}
                                                placeholder="Enter the vendor's registered email"
                                                required
                                                className="claim-modal__input"
                                            />
                                        </div>
                                    </>
                                ) : (
                                     <div className="claim-modal__form-section claim-modal__form-section--center">
                                        <p className="claim-modal__info-text">
                                            The primary verification email for <span className="claim-modal__vendor-name">{vendorToClaim?.name || 'this vendor'}</span> is not available.
                                        </p>
                                        <p className="claim-modal__info-text">
                                            Please use the alternative verification method below.
                                        </p>
                                     </div>
                                )}

                                {error && <div className="claim-modal__error">{error}</div>}

                                <SmsConsentCheckbox
                                    checked={smsNotificationsConsent}
                                    onChange={(e) => {
                                        setSmsNotificationsConsent(e.target.checked);
                                        setError(null);
                                    }}
                                />
                                <SmsConsentCheckbox
                                    name="smsMarketingConsent"
                                    checked={smsMarketingConsent}
                                    onChange={(e) => {
                                        setSmsMarketingConsent(e.target.checked);
                                        setError(null);
                                    }}
                                    variant="marketing"
                                />

                                <div className="claim-modal__actions">
                                    <button
                                        type="submit"
                                        className="claim-modal__button claim-modal__button--primary"
                                        disabled={loading || !canSubmit || !enteredEmail || !canAttemptStandardClaim}
                                    >
                                        {loading ? 'Verifying...' : 'Verify Email & Submit Claim'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setClaimStep('alternativeForm'); setError(null); }}
                                        className="claim-modal__button claim-modal__button--link"
                                        disabled={loading}
                                    >
                                        {canAttemptStandardClaim ? "I don't have access to this email" : "Proceed with Alternative Verification"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {claimStep === 'alternativeForm' && (
                            <form onSubmit={handleAlternativeSubmit}>
                                <p className="claim-modal__info-text">
                                    {canAttemptStandardClaim
                                      ? `Since you cannot verify via the registered email (${maskedVendorEmail}), please provide the following details for manual review. Our team will contact you.`
                                      : `Please provide the following details for manual review to claim "${vendorToClaim?.name || 'this profile'}". Our team will contact you.`}
                                </p>
                                {error && <div className="claim-modal__error">{error}</div>}

                                <div className="claim-modal__form-section">
                                    <label htmlFor="user_details" className="claim-modal__label">Your Full Name & Position:</label>
                                    <input type="text" id="user_details" name="user_details" value={alternativeFormData.user_details} onChange={handleAlternativeFormChange} placeholder="e.g., John Smith, Project Manager" className="claim-modal__input" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="contact_email" className="claim-modal__label">Contact Email:</label>
                                    <input type="email" id="contact_email" name="contact_email" value={alternativeFormData.contact_email} onChange={handleAlternativeFormChange} placeholder="Email address for us to contact you" className="claim-modal__input" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="contact_phone" className="claim-modal__label">Contact Phone (Optional):</label>
                                    <input type="tel" id="contact_phone" name="contact_phone" value={alternativeFormData.contact_phone} onChange={handleAlternativeFormChange} placeholder="Phone number for us to contact you" className="claim-modal__input"/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="social_media_links" className="claim-modal__label">Your Social Media Profiles (one per line):</label>
                                    <textarea id="social_media_links" name="social_media_links" value={alternativeFormData.social_media_links} onChange={handleAlternativeFormChange} placeholder="https://linkedin.com/in/yourprofile&#10;https://twitter.com/yourhandle" className="claim-modal__textarea" required/>
                                </div>
                                <div className="claim-modal__form-section">
                                    <label htmlFor="description" className="claim-modal__label">Reason for Claim / Explanation:</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={alternativeFormData.description}
                                        onChange={handleAlternativeFormChange}
                                        placeholder={canAttemptStandardClaim
                                            ? `Explain why you don't have access to the vendor's registered email (${maskedVendorEmail}) and why you should be granted access to this profile.`
                                            : `Explain your relationship to "${vendorToClaim?.name || 'this vendor'}" and why you should be granted access to this profile.`
                                        }
                                        className="claim-modal__textarea"
                                        rows={4}
                                        required
                                    />
                                </div>

                                <SmsConsentCheckbox
                                    checked={smsNotificationsConsent}
                                    onChange={(e) => {
                                        setSmsNotificationsConsent(e.target.checked);
                                        setError(null);
                                    }}
                                />
                                <SmsConsentCheckbox
                                    name="smsMarketingConsent"
                                    checked={smsMarketingConsent}
                                    onChange={(e) => {
                                        setSmsMarketingConsent(e.target.checked);
                                        setError(null);
                                    }}
                                    variant="marketing"
                                />

                                <div className="claim-modal__actions">
                                    <button type="submit" className="claim-modal__button claim-modal__button--primary" disabled={loading || !canSubmit || !isAlternativeFormComplete}>
                                        {loading ? 'Submitting Details...' : 'Submit Verification Details'}
                                    </button>
                                    {canAttemptStandardClaim && (
                                        <button type="button" onClick={() => { setClaimStep('verifyEmail'); setError(null); }} className="claim-modal__button claim-modal__button--link" disabled={loading}>
                                            Back to email verification
                                        </button>
                                    )}
                                </div>
                            </form>
                         )}

                        {claimStep === 'submitted' && (
                            <div className="claim-modal__success-content">
                                <img src="/assets/icon--sendok.svg" alt="Success" className="claim-modal__success-icon"/>
                                 <h2 className="claim-modal__success-title">Request Submitted!</h2>
                                 <p className="claim-modal__success-text">
                                     Your claim request for "{vendorToClaim?.name}" has been received. We will review it and get back to you soon.
                                 </p>
                                <div className="claim-modal__actions">
                                    <button type='button' onClick={handleClose} className="claim-modal__button claim-modal__button--primary">
                                        Okay
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}
